import { getNewDate } from './date';
import { properCase, createObjectWithKey } from './helpers';

/*
 * Relative Date Time
 * "-1d" = "1 Day Ago";
 * "Sd" = "Start of the day.""
 * "-1dS" = "Start of 1 day ago.""
 * "-1d,0112" = "1 Day Ago at 1:12 AM";
 * "-1d,-12h" = "1 Day and 12 hours ago";
 * "-1M" = "1 Month Ago";
 * "-1sM" = "Start of the Previous Month";
 */

const RELATIVE_UNIT_REGEX = /^([+-]?\d+)?([smhdwMy]?)([nS])?$/;
const RELATIVE_UNIT_REGEX_ES = /^([+-]?\d+)?([smhdwMy]?)(\/[smhdwMy])?$/;
const ES_RELATIVE_DATE_REGEX = /^(now)(([+-]?\d+)?([smhdwMy]?)(\/[smhdwMy])?)*$/;

export const TO_DATE_LABEL = 'ago to Date';

export const UNITS = {
  s: 'second',
  m: 'minute',
  h: 'hour',
  w: 'week',
  d: 'day',
  M: 'month',
  y: 'year',
};

export const UNIT_MODIFIERS = {
  n: 'now',
  e: 'exactly',
  S: 'start',
};

export const UNIT_VALUES = Object.keys(UNITS)
  .map((key) => createObjectWithKey(UNITS[key], key))
  .reduce((obj, unit) => {
    let newObj = obj;
    newObj = { ...newObj, ...unit };
    return newObj;
  });

export const UNIT_MODIFIER_VALUES = Object.keys(UNIT_MODIFIERS)
  .map((key) => createObjectWithKey(UNIT_MODIFIERS[key], key))
  .reduce((obj, unit) => {
    let newObj = obj;
    newObj = { ...newObj, ...unit };
    return newObj;
  });

/**
 * **parseDateUnit**_(dateString)_<br>
 * From a given single unit relative date string, it parses to an object containing the
 * amount, unit and unit modifier.
 * @param { String } dateString: The relative date unit string.
 * @returns { Object } The date object containing amount, unit and unit modifier.
 * @deprecated Use `getNewDate` from `utils/datetime`
 */
export const parseDateUnit = (dateString = '') => {
  const dateObj = RELATIVE_UNIT_REGEX.exec(dateString);
  if (dateObj) {
    const amountString = dateObj[1] || '';
    const amount = parseFloat(amountString.replace(/[+]/g, '')) || '';
    const unit = dateObj[2];
    const unitModifier = dateObj[3];
    const originalInput = dateObj[0];
    const isUnitModifierNow = unitModifier === UNIT_MODIFIER_VALUES.now;
    const isNow = (!amount && !unit) || isUnitModifierNow;

    return {
      amount,
      unit,
      unitModifier,
      originalInput,
      isUnitModifierNow,
      isNow,
    };
  }
  return {};
};

/**
 * **parseToDate**_(dateString)_<br>
 * From a given relative date string, it parses to a moment date object.
 * @param { String } dateString: The relative date string.
 * @returns { Moment } The moment date object.
 * @deprecated Use `getNewDate` from `utils/datetime`
 */
export const parseToDate = (dateString) => {
  let currentDate = '';

  if (dateString !== undefined && dateString !== null) {
    const timeUnits = dateString
      .replace(/([+-])/g, ',$1')
      .split(',')
      .map((timeQuantity) => parseDateUnit(timeQuantity));
    currentDate = getNewDate();

    timeUnits.forEach(({ amount, unit, unitModifier, isNow }) => {
      if (isNow) {
        currentDate = getNewDate({ date: currentDate, isNow: true });
      } else {
        if (unitModifier) {
          const unitModifierValue = UNIT_MODIFIERS[unitModifier];
          currentDate = getNewDate({ date: currentDate })[`${unitModifierValue}Of`](unit);
        }
        currentDate = getNewDate({ date: currentDate }).add(amount, unit);
      }
    });
  }
  return currentDate;
};

export const presets = {
  MONTH_TO_DATE: {
    label: 'Month to Date',
    startRange: `0${UNIT_VALUES.month}${UNIT_MODIFIER_VALUES.start}`,
    endRange: `${UNIT_MODIFIER_VALUES.now}`,
  },
  WEEK_TO_DATE: {
    label: 'Week to Date',
    startRange: `0${UNIT_VALUES.week}${UNIT_MODIFIER_VALUES.start}`,
    endRange: `${UNIT_MODIFIER_VALUES.now}`,
  },
  LAST_30_DAYS: {
    label: '30 Days ago',
    startRange: `-30${UNIT_VALUES.day}`,
    endRange: `${UNIT_MODIFIER_VALUES.now}`,
  },
  LAST_14_DAYS: {
    label: '14 Days ago',
    startRange: `-14${UNIT_VALUES.day}`,
    endRange: `${UNIT_MODIFIER_VALUES.now}`,
  },
  LAST_7_DAYS: {
    label: '7 Days ago',
    startRange: `-7${UNIT_VALUES.day}`,
    endRange: `${UNIT_MODIFIER_VALUES.now}`,
  },
  LAST_5_DAYS: {
    label: '5 Days ago',
    startRange: `-5${UNIT_VALUES.day}`,
    endRange: `${UNIT_MODIFIER_VALUES.now}`,
  },
  LAST_3_DAYS: {
    label: '3 Days ago',
    startRange: `-3${UNIT_VALUES.day}`,
    endRange: `${UNIT_MODIFIER_VALUES.now}`,
  },
  LAST_24_HOURS: {
    label: '24 Hours ago',
    startRange: `-24${UNIT_VALUES.hour}`,
    endRange: `${UNIT_MODIFIER_VALUES.now}`,
  },
  TODAY: {
    label: 'Today',
    startRange: `0${UNIT_VALUES.day}${UNIT_MODIFIER_VALUES.start}`,
    endRange: `${UNIT_MODIFIER_VALUES.now}`,
  },
  NEXT_24_HOURS: {
    label: '24 Hours from now',
    startRange: `${UNIT_MODIFIER_VALUES.now}`,
    endRange: `+24${UNIT_VALUES.hour}`,
  },
  NEXT_3_DAYS: {
    label: '3 Days from now',
    startRange: `${UNIT_MODIFIER_VALUES.now}`,
    endRange: `+3${UNIT_VALUES.day}`,
  },
  NEXT_5_DAYS: {
    label: '5 Days from now',
    startRange: `${UNIT_MODIFIER_VALUES.now}`,
    endRange: `+5${UNIT_VALUES.day}`,
  },
  NEXT_7_DAYS: {
    label: '7 Days from now',
    startRange: `${UNIT_MODIFIER_VALUES.now}`,
    endRange: `+7${UNIT_VALUES.day}`,
  },
  NEXT_14_DAYS: {
    label: '14 Days from now',
    startRange: `${UNIT_MODIFIER_VALUES.now}`,
    endRange: `+14${UNIT_VALUES.day}`,
  },
  NEXT_30_DAYS: {
    label: '30 Days from now',
    startRange: `${UNIT_MODIFIER_VALUES.now}`,
    endRange: `+30${UNIT_VALUES.day}`,
  },
};

export const defaultPresets = [
  presets.MONTH_TO_DATE,
  presets.WEEK_TO_DATE,
  presets.LAST_30_DAYS,
  presets.LAST_14_DAYS,
  presets.LAST_7_DAYS,
  presets.LAST_5_DAYS,
  presets.LAST_3_DAYS,
  presets.LAST_24_HOURS,
  presets.TODAY,
];

export const queryBuilderLastPresets = [
  presets.LAST_30_DAYS,
  presets.LAST_14_DAYS,
  presets.LAST_7_DAYS,
  presets.LAST_5_DAYS,
  presets.LAST_3_DAYS,
  presets.LAST_24_HOURS,
];

export const queryBuilderNextPresets = [
  presets.NEXT_24_HOURS,
  presets.NEXT_3_DAYS,
  presets.NEXT_5_DAYS,
  presets.NEXT_7_DAYS,
  presets.NEXT_14_DAYS,
  presets.NEXT_30_DAYS,
];

/**
 * **parseUnitToLabel**_(dateString)_<br>
 * From a given relative time unit, it generates a label.
 * @param { String } dateString: The relative date unit string.
 * @returns { String } The label for the relative date.
 */
export const parseUnitToLabel = (dateString) => {
  const { amount, unit, unitModifier, isNow } = parseDateUnit(dateString);

  /* Immediately return if value is now, else continue parsing the date. */
  if (isNow || (!unitModifier && !amount)) {
    return properCase(UNIT_MODIFIERS.n);
  }

  const amountString = amount.toString().replace('-', '');
  const unitModifierString = unitModifier ? `${properCase(UNIT_MODIFIERS[unitModifier])} of ` : '';
  const unitString = `${properCase(UNITS[unit])}`;
  const pluralString = amount !== 1 && amount !== -1 ? 's' : '';

  return `${unitModifierString}${amountString} ${unitString}${pluralString}`;
};

/**
 * **parseTimeRangeToLabelDef**_(startString, endString)_<br>
 * From a given start and end range relative time, it generates a label object containing the label
 * and if the range matches a preset.
 * @param { String } startString: The start range string relative time.
 * @param { String } endString: The end range string relative time.
 * @returns { Object } The labelDef object.
 */
export const parseTimeRangeToLabelDef = (startString, endString) => {
  const startObj = parseDateUnit(startString);
  const endObj = parseDateUnit(endString);
  const labelObj = {
    label: '',
    isPreset: false,
  };

  Object.keys(presets).forEach((key) => {
    const preset = presets[key];
    const { startRange, endRange } = preset;
    if (startString === startRange && endString === endRange) {
      labelObj.label = preset.label;
      labelObj.isPreset = true;
    }
  });

  // Continue parsing if not preset.
  if (labelObj.label === '') {
    // If start or end label is "now", don't show the label.
    const parsedStartLabel = parseUnitToLabel(startString);
    const parsedEndLabel = parseUnitToLabel(endString);
    const startLabel = startObj.isNow ? '' : `${parsedStartLabel} ago`;
    const endLabel = endObj.isNow ? '' : `${parsedEndLabel} from now`;
    const isNow = startLabel === endLabel;

    labelObj.label = isNow
      ? 'Now'
      : `${startLabel}${startLabel && endLabel ? ' to ' : ''}${endLabel}`;
  }
  return labelObj;
};

/**
 * **formatTimeUnitToES**_(dateString)_<br>
 * Parse a relative time unit string to ES notation.
 * @param { String } dateString: The relative time unit string to parse.
 * @returns { String } The parsed relative time unit string in ES notation.
 */
export const formatTimeUnitToES = (dateString) => {
  const { amount, unit, unitModifier } = parseDateUnit(dateString);
  const parsedAmount = amount > 0 ? `+${amount}` : amount;
  const unitModifierString = unitModifier && unit ? `/${unit}` : '';
  return `${parsedAmount}${parsedAmount ? unit : ''}${unitModifierString}`;
};

/**
 * **formatToES**_(dateString)_<br>
 * Parse a relative time string to ES notation.
 * @param { String } dateString: The relative time string to parse.
 * @returns { String } The parsed relative time string in ES notation.
 */
export const formatToES = (dateString) => {
  const base = 'now';
  const parsedDateArray = dateString
    .replace(/([+-])/g, ',$1')
    .split(',')
    .map((unit) => formatTimeUnitToES(unit));
  const dateIsDefined = parsedDateArray.every((date) => date !== 'undefined' && date !== 'null');
  if (dateIsDefined) {
    return `${base}${parsedDateArray.join('')}`;
  }
  return '';
};

/**
 * **parseDateUnitFromES**_(esString)_<br>
 * Parses an Elastic search relative date unit string .
 * @param { String } esString: The ES relative date unit string to parse.
 * @returns { String } The parsed ES relative date unit string.
 */
export const parseDateUnitFromES = (esString) => {
  const base = esString.replace(/now/g, '');
  const dateObj = RELATIVE_UNIT_REGEX_ES.exec(base);
  if (base && dateObj) {
    const amount = dateObj[1] || 0;
    const unit = dateObj[2] || '';
    const unitModifier = dateObj[3] || '';
    const unitModifierString = unitModifier ? `${unit ? '' : unitModifier.slice(-1)}S` : '';
    return `${amount}${unit}${unitModifierString}`;
  }
  return '';
};

/**
 * **parseDateUnitFromES**_(esString)_<br>
 * Parses an Elastic search relative date string .
 * @param { String } esString: The ES relative date string to parse.
 * @returns { String } The parsed ES relative date string.
 */
export const parseFromES = (esString) => {
  const dateUnits = esString.replace(/([+-])/g, ',$1').split(',');
  const output = dateUnits.map(parseDateUnitFromES);
  return output.join('');
};

/**
 * **parseFromESToDateRange**_(esString)_<br>
 * Parses a single Elastic search relative date to a date range.
 * @param { String } esString: The Elastic Search relative date string to parse.
 * @returns { Object } The parsed Elastic Search relative date range.
 */
export const parseFromESToDateRange = (esString) => {
  const parsedString = parseFromES(esString);

  const isPositive = parsedString.startsWith('+');
  const startRange = isPositive ? UNIT_MODIFIER_VALUES.now : parsedString;
  const endRange = isPositive ? parsedString : UNIT_MODIFIER_VALUES.now;
  return { startRange, endRange };
};

/**
 * **isRelativeESDate**_(esString)_<br>
 * Checks if given string is an Elastic Search relative date.
 * @param { String } esString: The Elastic Search relative date string to check.
 * @returns { Boolean } If the given input in an Elastic Search relative date.
 */
export const isRelativeESDate = (esString) => ES_RELATIVE_DATE_REGEX.test(esString);

/**
 * **relativeRangeToAbsolute**_(startRange, endRange)_<br>
 * Convert a relative date range to absolute date range.
 * @param { String } startRange: The relative start range.
 * @param { String } endRange: The relative end range.
 * @returns { Object } The absolute time range object { startDate, startTime, endDate, endTime }.
 */
export const relativeRangeToAbsolute = (startRange, endRange) => ({
  startDate: parseToDate(startRange),
  startTime: parseToDate(startRange),
  endDate: parseToDate(endRange),
  endTime: parseToDate(endRange),
});
